<template>
  <header class="bg-white shadow">
    <div class="container mx-auto px-4 py-4 flex justify-between items-center">
      <div class="flex items-center">
        <div class="mr-4 lg:mr-6">
          <router-link to="/dashboard" class="flex items-center">
<!--            <img src="/logo.png" alt="Logo" class="h-8 w-auto" onerror="this.src='/favicon.ico'" />-->
            <span class="ml-2 text-xl font-bold text-gray-900">{{ $t('common.app_name') }}</span>
          </router-link>
        </div>
        <nav class="hidden md:flex space-x-6">
          <router-link 
            v-for="(item, index) in navigationItems" 
            :key="index"
            :to="item.path"
            class="text-gray-600 hover:text-gray-900 hover:underline"
            active-class="text-blue-600 font-medium"
          >
            {{ item.name }}
          </router-link>
        </nav>
      </div>

            <!-- Language Selector -->
            <div class="hidden md:flex md:items-center md:ml-6">
              <LanguageSelector :darkMode="isDarkMode" />
            </div>

      <div class="flex items-center">
        <!-- User menu dropdown -->
        <div class="relative ml-3">
          <div>
            <button 
              @click="toggleUserMenu"
              class="flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <div class="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600">
                <span v-if="user?.name">{{ user.name.charAt(0) }}</span>
                <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd" />
                </svg>
              </div>
              <span class="ml-2 hidden md:block">{{ user?.name || 'User' }}</span>
              <svg class="ml-1 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
              </svg>
            </button>
          </div>

          <!-- Dropdown menu -->
          <div 
            v-if="isUserMenuOpen"
            class="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50"
          >
            <div class="py-1">
              <router-link 
                to="/profile"
                class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                @click="isUserMenuOpen = false"
              >
                {{ t('profile.title') }}
              </router-link>
              <button 
                @click="logout"
                class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                {{ t('common.logout') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Mobile menu button -->
        <button 
          type="button"
          @click="handleMobileMenuToggle"
          class="md:hidden ml-2 p-2 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <svg v-if="!isMobileMenuOpen" class="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
          <svg v-else class="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Mobile menu -->
    <div v-if="isMobileMenuOpen" class="md:hidden absolute top-16 inset-x-0 z-10 bg-white shadow-lg">
      <div class="pt-2 pb-3 space-y-1 px-4">
        <!-- Language Selector Mobile -->
        <div class="px-3 py-2 mb-2 border-b border-gray-200">
          <LanguageSelector :darkMode="isDarkMode" />
        </div>

        <router-link 
          v-for="(item, index) in navigationItems" 
          :key="index"
          :to="item.path"
          class="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
          active-class="bg-blue-50 text-blue-700"
          @click="isMobileMenuOpen = false"
        >
          {{ item.name }}
        </router-link>
      </div>
    </div>
  </header>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../../stores/auth'
import LanguageSelector from './LanguageSelector.vue'

const { t } = useI18n()

const router = useRouter()
const authStore = useAuthStore()

// User data
const user = ref(authStore.user)

// Navigation states
const isUserMenuOpen = ref(false)

// Theme state
const isDarkMode = computed(() => {
  return document.documentElement.classList.contains('dark')
})
const isMobileMenuOpen = ref(false)

// Navigation items
const navigationItems = computed(() => [
  { name: t('dashboard.title'), path: '/dashboard' },
  { name: t('devices.title'), path: '/devices' }
])

// Methods
const toggleUserMenu = () => {
  isUserMenuOpen.value = !isUserMenuOpen.value
}

const handleMobileMenuToggle = () => {
  console.log('Toggling mobile menu')
  isMobileMenuOpen.value = !isMobileMenuOpen.value
}

const logout = async () => {
  await authStore.logout()
  router.push('/login')
}

// Close dropdown when clicking outside
/*const handleClickOutside = (event) => {
  // Only handle user menu dropdown
  const userMenuButton = event.target.closest('button[class*="rounded-full"]')
  const userMenuContent = event.target.closest('.origin-top-right')
  if (isUserMenuOpen.value && !userMenuButton && !userMenuContent) {
    isUserMenuOpen.value = false
  }

  // For mobile menu, handle in a separate event listener
}*/

// Close dropdown when clicking outside
const handleClickOutside = (event) => {
  // Handle user menu dropdown
  if (isUserMenuOpen.value && !event.target.closest('.relative.ml-3')) {
    isUserMenuOpen.value = false
  }

  // Handle mobile menu
  /*if (isMobileMenuOpen.value &&
      !event.target.closest('button[class*="md:hidden"]') && 
      !event.target.closest('.md\\:hidden')) {
    isMobileMenuOpen.value = false
  }*/
}

// Lifecycle hooks
onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>
